import { Component, Input, Output, EventEmitter, OnInit, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../shared/material.module';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData, Chart } from 'chart.js';
import { PyramidEntitySummary } from '../../../models/pyramidEntitySummary';
import { MatCardModule } from '@angular/material/card';

export interface DeleteEntityYearEvent {
  entityId: string;
  entityType: string;
  year: number;
}

@Component({
  selector: 'app-pyramidal-item-card',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    BaseChartDirective,
    MatCardModule
  ],
  templateUrl: './pyramidal-item-card.component.html',
  styleUrl: './pyramidal-item-card.component.scss'
})
export class PyramidalItemCardComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective<'bar'> | undefined;
  @Input() entitySummary!: PyramidEntitySummary;
  @Input() showTextView = false;
  @Output() deleteEntityYear = new EventEmitter<DeleteEntityYearEvent>();

  public barChartType = 'bar' as const;
  public barChartData!: ChartData<'bar'>;
  public barChartOptions!: ChartConfiguration<'bar'>['options'];

  ngOnInit() {
    this.initializeChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reinitialize chart when entitySummary changes
    if (changes['entitySummary'] && !changes['entitySummary'].firstChange) {
      this.initializeChart();
      // Update the chart if it exists
      setTimeout(() => {
        this.chart?.update();
      }, 0);
    }
  }

  ngAfterViewInit() {
    // Update chart after view is initialized to apply colors properly
    setTimeout(() => {
      this.chart?.update();
    }, 0);
  }

  private initializeChart() {
    if (!this.entitySummary || !this.entitySummary.yearPercentages.length) {
      return;
    }

    // Get CSS custom properties for colors from body element
    const styles = getComputedStyle(document.body);
    const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

    // Helper function to parse light-dark() CSS function
    const parseColor = (cssValue: string, fallback: string): string => {
      if (!cssValue) return fallback;

      // Check if it's a light-dark() function
      const lightDarkMatch = cssValue.match(/light-dark\(([^,]+),\s*([^)]+)\)/);
      if (lightDarkMatch) {
        // Return dark color if dark mode, otherwise light color
        return isDarkMode ? lightDarkMatch[2].trim() : lightDarkMatch[1].trim();
      }

      return cssValue;
    };

    const secondaryColor = parseColor(styles.getPropertyValue('--mat-sys-secondary').trim(), '#6750A4');
    const cardBackgroundColor = parseColor(styles.getPropertyValue('--mat-sys-surface-container-lowest').trim(), '#F5F5F5');
    const onSurfaceColor = parseColor(styles.getPropertyValue('--mat-sys-on-surface').trim(), '#1C1B1F');
    const surfaceDimColor = parseColor(styles.getPropertyValue('--mat-sys-surface-dim').trim(), '#E0E0E0');
    const onPrimaryFixedColor = parseColor(styles.getPropertyValue('mat-sys-on-primary-fixed').trim(), '#000000');

    // Extract labels (years) and data (percentages) from yearPercentages
    const labels = this.entitySummary.yearPercentages.map(yp => yp.year.toString());
    const data = this.entitySummary.yearPercentages.map(yp => yp.percentage);

    this.barChartData = {
      labels: labels,
      datasets: [
        {
          data: data,
          label: 'Percentage',
          backgroundColor: secondaryColor,
          borderColor: secondaryColor,
          hoverBackgroundColor: secondaryColor,
          hoverBorderColor: secondaryColor,
        }
      ]
    };

    this.barChartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2,
      scales: {
        x: {
          grid: {
            color: onPrimaryFixedColor,
            display: false
          },
          ticks: {
            color: onPrimaryFixedColor,
            font: {
              family: 'Roboto, sans-serif',
              size: 12
            }
          },
          border: {
            color: onPrimaryFixedColor
          }
        },
        y: {
          beginAtZero: true,
          min: 0,
          max: 100,
          grid: {
            color: onPrimaryFixedColor,
            display: true
          },
          ticks: {
            color: onPrimaryFixedColor,
            font: {
              family: 'Roboto, sans-serif',
              size: 12
            },
            callback: function(value) {
              return value + '%';
            }
          },
          border: {
            color: onPrimaryFixedColor
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        datalabels: {
          display: false
        },
        tooltip: {
          backgroundColor: cardBackgroundColor,
          titleColor: onSurfaceColor,
          bodyColor: onSurfaceColor,
          borderColor: surfaceDimColor,
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              return 'Percentage: ' + context.parsed.y + '%';
            }
          }
        }
      }
    };
  }

  get sortedYearPercentages() {
    if (!this.entitySummary?.yearPercentages) {
      return [];
    }
    return [...this.entitySummary.yearPercentages].sort((a, b) => b.year - a.year);
  }

  onDeleteYear(year: number, event: Event) {
    event.stopPropagation();
    this.deleteEntityYear.emit({
      entityId: this.entitySummary.entityId,
      entityType: this.entitySummary.entityType,
      year
    });
  }
}
